import {ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, OnDestroy, OnInit} from '@angular/core';
import {CommonModule} from '@angular/common';
import {RouterModule} from '@angular/router';
import {FormsModule} from '@angular/forms';
import {from, of, Subject} from 'rxjs';
import {catchError, finalize, mergeMap, take, takeUntil, tap} from 'rxjs/operators';
import {
    DsBreadcrumbModule,
    DsButtonModule,
    DsCheckboxModule,
    DsFormFieldModule,
    DsIconModule,
    DsLoadingAreaConfiguration,
    DsLoadingAreaService,
    DsProgressCircleModule,
    DsRowData,
    DsSelectModule,
    DsTableModule,
    DsTabsModule,
    DsToastModel,
    DsToastService,
} from '@bmw-ds/components';

import {BreadcrumbComponent} from 'src/app/components/breadcrumb/breadcrumb.component';
import {NatisDocsService} from '../../services/natis.service';
import {StockService} from '../../services/stock.service';
import {AuthService} from '../../services/auth.service';
import {UserInfoService} from '../../services/user-info.service';
import {CriteriaResponse} from 'src/app/models/criteria.model';
import {NatisDocsRequest, NatisDocsResponse, NatisReleaseRequest} from 'src/app/models/natis-docs.model';
import {COMMON_IMPORTS} from '../../shared/common-imports';
import {UnitOverviewLinkPipe} from '../../unit-overview.pipe';

@Component({
    selector: 'app-natis-documents',
    standalone: true,
    imports: [
        CommonModule,
        RouterModule,
        FormsModule,
        DsIconModule,
        DsButtonModule,
        DsTabsModule,
        DsBreadcrumbModule,
        DsTableModule,
        DsSelectModule,
        DsFormFieldModule,
        DsCheckboxModule,
        DsProgressCircleModule,
        BreadcrumbComponent,
        COMMON_IMPORTS,
        UnitOverviewLinkPipe
    ],
    templateUrl: './natis-documents.component.html',
    styleUrl: './natis-documents.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class NatisDocumentsComponent implements OnInit, OnDestroy {
    public criteriaData!: CriteriaResponse;
    public natisRows: DsRowData[] = [];
    public loadingAreaConfig: DsLoadingAreaConfiguration = {id: 'bodyLoading', loadingTitle: 'Loading...'};
    public searchTerm: string = '';

    private allNatisRows: DsRowData[] = [];

    private readonly natisService = inject(NatisDocsService);
    private readonly stockService = inject(StockService);
    private readonly loadingAreaService = inject(DsLoadingAreaService);
    private readonly cdr = inject(ChangeDetectorRef);
    private readonly authService = inject(AuthService);
    private readonly userInfoService = inject(UserInfoService);
    private readonly toastService = inject(DsToastService);

    private readonly destroy$ = new Subject<void>();
    protected firstSearch: boolean = true;

    public ngOnInit(): void {
        this.authService.isAuthenticated().pipe(take(1)).subscribe((isAuthenticated) => {
            if (!isAuthenticated) {
                window.location.href = this.authService.getAuthUrl();
                return;
            }

            this.loadingAreaService.addLoadingArea(this.loadingAreaConfig);
            const dealerCode = localStorage.getItem('dealerCode') || '';
            this.loadInitialData(dealerCode);
        });
    }

    public ngOnDestroy(): void {
        this.loadingAreaService.removeLoadingArea(this.loadingAreaConfig.id);
        this.destroy$.next();
        this.destroy$.complete();
    }

    public trackByVin(index: number, item: DsRowData): string | number {
        return item['zvin'];
    }

    public onSearch(): void {
        const term = this.searchTerm ? this.searchTerm.toLowerCase().trim() : '';

        if (!term) {
            this.natisRows = [...this.allNatisRows];
        } else {
            this.natisRows = this.allNatisRows.filter(row => {
                const vin = String(row['zvin'] || '').toLowerCase();
                const invoice = String(row['zinvoice'] || '').toLowerCase();
                const model = String(row['model'] || '').toLowerCase();
                const interior = String(row['interior'] || '').toLowerCase();
                const exterior = String(row['exterior'] || '').toLowerCase();

                return vin.includes(term) ||
                    invoice.includes(term) ||
                    model.includes(term) ||
                    interior.includes(term) ||
                    exterior.includes(term);
            });
        }

        this.cdr.markForCheck();
    }

    public clearSearch(): void {
        this.searchTerm = '';
        this.onSearch();
    }

    private applyNatisDocs(data: NatisDocsResponse): void {
        this.firstSearch = false;
        this.allNatisRows = (data.it_otab || []).map((item) => ({
            image: '',
            model: item.zmodeldesc,
            interior: item.zint_desc,
            exterior: item.zext_desc,
            zinvoice: item.zinvoice,
            view: this.getReleaseStatus(item.zreleased),
            zvin: item.zvin,
            zreleased: item.zreleased,
        }));

        this.natisRows = [...this.allNatisRows];
        this.cdr.detectChanges();
        if (this.allNatisRows.length > 0) {
            setTimeout(() => {
                this.loadImagesConcurrently();
            }, 0);
        }
    }

    private loadNatisDocs(dealerCode: string): void {
        const userId = this.getCurrentUserId();
        const payload: NatisDocsRequest = {cNumber: userId.toUpperCase(), dealerCode};

        this.natisService.getNatisDocs(payload)
            .pipe(
                takeUntil(this.destroy$),
                tap((data: NatisDocsResponse) => this.applyNatisDocs(data)),
                catchError((error) => {
                    console.error('Error fetching Natis Docs', error);
                    this.addToast('critical', 'Failed to load documents.');
                    return of(null);
                }),
                finalize(() => {
                    this.cdr.markForCheck();
                })
            )
            .subscribe();
    }

    private loadImagesConcurrently(): void {
        from(this.allNatisRows).pipe(
            takeUntil(this.destroy$),
            mergeMap((row: any) => {
                if (!row['zvin']) {
                    return of(null);
                }

                return this.stockService.getTableCosyUrl({VIN: row['zvin']}).pipe(
                    catchError(() => of({exturl: ''})),
                    tap((response) => {
                        if (response?.exturl) {
                            row['image'] = response.exturl;
                            this.cdr.markForCheck();
                        }
                    })
                );
            }, 5)
        ).subscribe();
    }

    public getReleaseStatus(zreleased: string): string {
        const statusMap: { [key: string]: string } = {
            'X': 'Released',
            'R': 'NaTIS reported an Error',
            'Y': 'NaTIS busy with Request'
        };
        return statusMap[zreleased] || 'Ready for Release';
    }

    public downloadDocument(vin: string | number): void {
        const vinStr = String(vin);
        if (!vinStr) {
            return;
        }

        const bodyNo = vinStr.slice(-8);

        this.natisService.getNatisDocInfo({iv_vin: vinStr, iv_body: bodyNo})
            .pipe(
                takeUntil(this.destroy$),
                finalize(() => {
                    this.cdr.markForCheck();
                })
            )
            .subscribe({
                next: (response) => {
                    if (response.ev_pdf) {
                        this.triggerPdfDownload(response.ev_pdf, vinStr);
                    } else {
                        this.addToast('neutral', 'No PDF document available.');
                    }
                },
                error: (err) => {
                    console.error('Download failed', err);
                    this.addToast('critical', 'Download failed.');
                }
            });
    }

    private triggerPdfDownload(base64Data: string, fileName: string): void {
        try {
            const byteCharacters = atob(base64Data);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            const blob = new Blob([byteArray], {type: 'application/pdf'});

            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `${fileName}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (e) {
            console.error('Blob creation failed', e);
            this.addToast('critical', 'Error processing file.');
        }
    }

    public releaseVehicle(rowElement: string | number): void {
        const vinStr = String(rowElement);
        if (!vinStr) {
            return;
        }

        const userId = this.getCurrentUserId();
        const dealerCode = localStorage.getItem('dealerCode') || '';

        const request: NatisReleaseRequest = {
            cNumber: userId.toUpperCase(),
            dealerCode,
            vin: vinStr,
            zbody: vinStr.slice(-8),
        };

        this.natisService.releaseVehicle(request)
            .pipe(
                takeUntil(this.destroy$),
                finalize(() => {
                    this.cdr.markForCheck();
                })
            )
            .subscribe({
                next: (response) => {
                    if (response.error_code && response.error_code !== '00') {
                        this.addToast('critical', `Release failed: ${response.message}`);
                    } else {
                        this.addToast('positive', response.message);
                        this.loadNatisDocs(dealerCode);
                    }
                },
                error: (err) => {
                    console.error('Vehicle release failed', err);
                    this.addToast('critical', 'System error during release.');
                }
            });
    }

    private addToast(tone: 'critical' | 'positive' | 'neutral', toastText: string): void {
        this.toastService.pushToast({
            id: `toast-${Date.now()}`,
            tone,
            toastText,
        } as DsToastModel);
    }

    private loadInitialData(dealerCode: string): void {
        const userId = this.getCurrentUserId();
        const payload: NatisDocsRequest = {cNumber: userId.toUpperCase(), dealerCode};

        this.natisService.getNatisDocs(payload)
            .pipe(
                takeUntil(this.destroy$),
                tap((data: NatisDocsResponse) => this.applyNatisDocs(data)),
                catchError((error) => {
                    console.error('Error loading initial data', error);
                    this.addToast('critical', 'Failed to load documents.');
                    this.loadingAreaService.removeLoadingArea(this.loadingAreaConfig.id);
                    return of(null);
                }),
                finalize(() => {
                    this.loadingAreaService.removeLoadingArea(this.loadingAreaConfig.id);
                    this.cdr.markForCheck();
                })
            )
            .subscribe();
    }

    private getCurrentUserId(): string {
        return this.userInfoService.getUserId();
    }
}
